/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { ITrustpilotWidgetData } from './trustpilot-widget.data';
import { ITrustpilotWidgetProps } from './trustpilot-widget.props.autogenerated';
import { ITrustpilotWidgetViewProps } from './trustpilot-widget.view';

interface IWidgetConfig {
    templateId?: string;
    businessUnitId?: string;
    height?: string;
    width?: string;
    tags?: string;
    reviewLanguages?: string;
    dataLocale?: string;
}

/**
 *
 * TrustpilotWidget component
 * @extends {React.PureComponent<ITrustpilotWidgetProps<ITrustpilotWidgetData>>}
 */
class TrustpilotWidget extends React.PureComponent<ITrustpilotWidgetProps<ITrustpilotWidgetData>> {
    private tpRef: React.RefObject<HTMLDivElement>;

    constructor(props: ITrustpilotWidgetProps<ITrustpilotWidgetData>) {
        super(props);
        this.tpRef = React.createRef<HTMLDivElement>();
    }

    public componentDidMount(): void {
        useTpScript();
        if (window.Trustpilot) {
            window.Trustpilot.loadFromElement(this.tpRef.current, true);
        }
    }

    public render(): JSX.Element | null {
        const { config, context } = this.props;
        const businessUnitId = config.businessUnitId ?? '';
        const widgetType = config.widgetType ?? 'micro';

        const widgetConfig: IWidgetConfig = getWidgetConfigs(widgetType, context.request.locale, config.businessUnitId ?? '');
        const trustPilotLink = config.trustPilotLink ?? '';

        const viewProps: ITrustpilotWidgetViewProps = {
            ...this.props,
            ref: this.tpRef,
            businessUnitId,
            widgetType,
            widgetConfig: {
                widgetId: config.widgetId ?? '',
                businessUnitId: businessUnitId,
                trustPilotLink: trustPilotLink,
                templateId: widgetConfig.templateId,
                height: config.height?.trim() ? config.height : widgetConfig.height,
                width: config.width?.trim() ? config.width : widgetConfig.width,
                tags: config.tags,
                dataLocale: widgetConfig.dataLocale,
                reviewLanguages: widgetConfig.reviewLanguages
            }
        };

        return this.props.renderView(viewProps);
    }
}

function useTpScript(): void {
    const existing = document.querySelector('script[src*="tp.widget.bootstrap.min.js"]');
    if (!existing) {
        const tpScript = document.createElement('script');
        tpScript.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
        tpScript.async = true;
        tpScript.type = 'text/javascript';
        document.head.appendChild(tpScript);
    }
}

function getWidgetConfigs(widgetType: string, locale: string, businessUnitId: string): IWidgetConfig {
    const baseConfigs: IWidgetConfig = {
        reviewLanguages: locale,
        dataLocale: getLocaleBcpFormat(locale),
        businessUnitId: businessUnitId
    };

    let widgetConfig: IWidgetConfig = {};
    switch (widgetType) {
        case 'micro': {
            widgetConfig = {
                ...baseConfigs,
                templateId: '5419b637fa0340045cd0c936',
                height: '20px',
                width: '100%'
            };
            break;
        }
        case 'carousel': {
            widgetConfig = {
                ...baseConfigs,
                templateId: '53aa8912dec7e10d38f59f36',
                height: '140px',
                width: '100%'
            };
            break;
        }
    }
    return widgetConfig;
}

function getLocaleBcpFormat(locale: string): string {
    const localeMap: { [key: string]: string } = {
        da: 'da-DK',
        de: 'de-DE',
        nl: 'nl-NL'
    };
    return localeMap[locale] || locale;
}

export default TrustpilotWidget;
